import { useCallback, useMemo, useState } from 'react';
import {
  HsmContact,
  HsmContactRequest,
  HsmBulkContactRequest,
  HsmContactFilter,
  HsmContactSortKey
} from '../types';
import * as hsmService from '../services/hsm.service';
import { withAuthRetry } from '../utils/withAuthRetry';

export interface UseHsmContactsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmContactsReturn {
  // State
  contacts: HsmContact[];
  contactsLoading: boolean;
  contactsFilter: HsmContactFilter;
  setContactsFilter: (filter: HsmContactFilter) => void;
  contactsSearch: string;
  setContactsSearch: (search: string) => void;
  contactsSort: { column: HsmContactSortKey; direction: 'asc' | 'desc' };
  toggleContactsSort: (column: HsmContactSortKey) => void;
  selectedContact: HsmContact | null;
  contactDialogOpen: boolean;
  setContactDialogOpen: (open: boolean) => void;
  bulkImportDialogOpen: boolean;
  setBulkImportDialogOpen: (open: boolean) => void;

  // Form state
  contactPhoneNumber: string;
  setContactPhoneNumber: (value: string) => void;
  contactName: string;
  setContactName: (value: string) => void;
  contactCompany: string;
  setContactCompany: (value: string) => void;
  contactCpfCnpj: string;
  setContactCpfCnpj: (value: string) => void;
  contactEmail: string;
  setContactEmail: (value: string) => void;
  contactCampaignDay: string;
  setContactCampaignDay: (value: string) => void;
  contactSaving: boolean;

  // Actions
  fetchContacts: () => Promise<{ success: boolean; error?: string }>;
  createContact: (data: HsmContactRequest) => Promise<{ success: boolean; error?: string }>;
  updateContact: (contactId: number, data: Partial<HsmContactRequest>) => Promise<{ success: boolean; error?: string }>;
  deleteContact: (contactId: number) => Promise<{ success: boolean; error?: string }>;
  activateContact: (contactId: number) => Promise<{ success: boolean; error?: string }>;
  deactivateContact: (contactId: number) => Promise<{ success: boolean; error?: string }>;
  bulkImportContacts: (data: HsmBulkContactRequest) => Promise<{ success: boolean; error?: string; result?: { created: number; skipped: number } }>;
  openNewContactDialog: () => void;
  openEditContactDialog: (contact: HsmContact) => void;
  resetContactForm: () => void;
  loadContactToForm: (contact: HsmContact) => void;
}

export function useHsmContacts({
  accessToken,
  refreshToken,
  attemptRefresh,
  canManageHsm
}: UseHsmContactsProps): UseHsmContactsReturn {
  // State
  const [contacts, setContacts] = useState<HsmContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsFilter, setContactsFilter] = useState<HsmContactFilter>('all');
  const [contactsSearch, setContactsSearch] = useState('');
  const [contactsSort, setContactsSort] = useState<{ column: HsmContactSortKey; direction: 'asc' | 'desc' }>({
    column: 'createdAt',
    direction: 'desc'
  });
  const [selectedContact, setSelectedContact] = useState<HsmContact | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);

  // Form state
  const [contactPhoneNumber, setContactPhoneNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactCompany, setContactCompany] = useState('');
  const [contactCpfCnpj, setContactCpfCnpj] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCampaignDay, setContactCampaignDay] = useState('1');
  const [contactSaving, setContactSaving] = useState(false);

  // Filtered & sorted contacts
  const filteredContacts = useMemo(() => {
    if (!Array.isArray(contacts)) return [];
    let result = [...contacts];

    // Apply status filter
    if (contactsFilter !== 'all') {
      const statusMap: Record<string, string> = {
        active: 'ACTIVE',
        pending: 'PENDING',
        paused: 'PAUSED',
        completed: 'COMPLETED',
        opted_out: 'OPTED_OUT'
      };
      result = result.filter((c) => c.status === statusMap[contactsFilter]);
    }

    // Apply search
    if (contactsSearch.trim()) {
      const searchLower = contactsSearch.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.phoneNumber.toLowerCase().includes(searchLower) ||
          (c.name && c.name.toLowerCase().includes(searchLower)) ||
          (c.company && c.company.toLowerCase().includes(searchLower))
      );
    }

    // Apply sort
    result.sort((a, b) => {
      const { column, direction } = contactsSort;
      let aVal: string | number | null = null;
      let bVal: string | number | null = null;

      switch (column) {
        case 'phoneNumber':
          aVal = a.phoneNumber;
          bVal = b.phoneNumber;
          break;
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'campaignDay':
          aVal = a.campaignDay;
          bVal = b.campaignDay;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'createdAt':
          aVal = a.createdAt;
          bVal = b.createdAt;
          break;
        case 'lastMessageSent':
          aVal = a.lastMessageSent || '';
          bVal = b.lastMessageSent || '';
          break;
      }

      if (aVal === null || bVal === null) return 0;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const comparison = String(aVal).localeCompare(String(bVal));
      return direction === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [contacts, contactsFilter, contactsSearch, contactsSort]);

  const toggleContactsSort = useCallback((column: HsmContactSortKey) => {
    setContactsSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // API calls
  const fetchContacts = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageHsm) {
      return { success: false, error: 'Nao autorizado' };
    }

    setContactsLoading(true);

    try {
      const data = await withAuthRetry(
        () => hsmService.fetchContacts(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setContacts(Array.isArray(data) ? data : []);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar contatos'
      };
    } finally {
      setContactsLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageHsm]);

  const createContact = useCallback(
    async (data: HsmContactRequest): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setContactSaving(true);

      try {
        await withAuthRetry(
          () => hsmService.createContact(data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao criar contato'
        };
      } finally {
        setContactSaving(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  const updateContact = useCallback(
    async (contactId: number, data: Partial<HsmContactRequest>): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setContactSaving(true);

      try {
        await withAuthRetry(
          () => hsmService.updateContact(contactId, data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao atualizar contato'
        };
      } finally {
        setContactSaving(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  const deleteContact = useCallback(
    async (contactId: number): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        await withAuthRetry(
          () => hsmService.deleteContact(contactId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao excluir contato'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  const activateContact = useCallback(
    async (contactId: number): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        await withAuthRetry(
          () => hsmService.activateContact(contactId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao ativar contato'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  const deactivateContact = useCallback(
    async (contactId: number): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        await withAuthRetry(
          () => hsmService.deactivateContact(contactId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao desativar contato'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  const bulkImportContacts = useCallback(
    async (data: HsmBulkContactRequest): Promise<{ success: boolean; error?: string; result?: { created: number; skipped: number } }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setContactSaving(true);

      try {
        const result = await withAuthRetry(
          () => hsmService.bulkImportContacts(data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchContacts();
        return { success: true, result: { created: result.created, skipped: result.skipped } };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao importar contatos'
        };
      } finally {
        setContactSaving(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchContacts]
  );

  // Form helpers
  const resetContactForm = useCallback(() => {
    setContactPhoneNumber('');
    setContactName('');
    setContactCompany('');
    setContactCpfCnpj('');
    setContactEmail('');
    setContactCampaignDay('1');
    setSelectedContact(null);
  }, []);

  const loadContactToForm = useCallback((contact: HsmContact) => {
    setContactPhoneNumber(contact.phoneNumber);
    setContactName(contact.name || '');
    setContactCompany(contact.company || '');
    setContactCpfCnpj(contact.cpfCnpj || '');
    setContactEmail(contact.email || '');
    setContactCampaignDay(String(contact.campaignDay));
    setSelectedContact(contact);
  }, []);

  // Dialog handlers
  const openNewContactDialog = useCallback(() => {
    resetContactForm();
    setContactDialogOpen(true);
  }, [resetContactForm]);

  const openEditContactDialog = useCallback(
    (contact: HsmContact) => {
      loadContactToForm(contact);
      setContactDialogOpen(true);
    },
    [loadContactToForm]
  );

  return {
    // State
    contacts: filteredContacts,
    contactsLoading,
    contactsFilter,
    setContactsFilter,
    contactsSearch,
    setContactsSearch,
    contactsSort,
    toggleContactsSort,
    selectedContact,
    contactDialogOpen,
    setContactDialogOpen,
    bulkImportDialogOpen,
    setBulkImportDialogOpen,

    // Form state
    contactPhoneNumber,
    setContactPhoneNumber,
    contactName,
    setContactName,
    contactCompany,
    setContactCompany,
    contactCpfCnpj,
    setContactCpfCnpj,
    contactEmail,
    setContactEmail,
    contactCampaignDay,
    setContactCampaignDay,
    contactSaving,

    // Actions
    fetchContacts,
    createContact,
    updateContact,
    deleteContact,
    activateContact,
    deactivateContact,
    bulkImportContacts,
    openNewContactDialog,
    openEditContactDialog,
    resetContactForm,
    loadContactToForm
  };
}
